/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import React, { useState, useEffect, useRef } from "react"
import { SectionGrid, Flex, Grid } from "frontend/src/components/Layout"
import { Icon } from "frontend/src/components/Icon"
import Img from "gatsby-image"
import { getFluidFromAsset } from "frontend/src/utils/gatsbyImageHelpers"
import { displayCent } from "frontend/src/utils/displayCent"
import { Touchable } from "frontend/src/components/Touchable"
import { useCart } from "frontend/src/utils/useCart"
import { FiShoppingCart } from "react-icons/fi"

// const Nbsp = () => "\u00A0"

const RiIcon = ({ Component, size, color }) => (
  <React.Fragment>
    <span sx={{ display: ["inline", "none", "none"] }}>
      <Component color={color} size={size[0]} />
    </span>
    <span sx={{ display: ["none", "inline", "none"] }}>
      <Component color={color} size={size[1]} />
    </span>
    <span sx={{ display: ["none", "none", "inline"] }}>
      <Component color={color} size={size[2]} />
    </span>
  </React.Fragment>
)

/* show single tag */
function Tag({ tag }) {
  const color = "almostBlack"
  if (typeof tag === "string" && tag.length > 0) {
    return (
      <div
        sx={{
          backgroundColor: color,
          color: "background",
          fontWeight: "regular",
          fontSize: "12px",
          ml: 0,
          textAlign: "left",
          px: 3,
          lineHeight: 1.0,
          py: 2,
          mb: 1,
        }}
      >
        {tag}
      </div>
    )
  }
  return null
}

function ProductImage({ image, tags = [], background = undefined, page }) {
  const containerRef = useRef(null)
  const [height, setHeight] = useState("300px")
  useEffect(() => {
    try {
      const adjustImages = () => {
        if (containerRef && containerRef.current) {
          const myHeight = `${containerRef.current.offsetWidth}px`
          setHeight(myHeight)
        }
      }
      if (window) {
        window.addEventListener("load", adjustImages)
        window.addEventListener("resize", adjustImages)
      }
      adjustImages()
    } catch (err) {
      console.error(err)
    }
  }, [])
  if (page) {
    return (
      <Touchable
        sx={{
          width: "100%",
          height: height,
          mt: 6,
          mb: 5,
          display: "grid",
          gridTemplateColumns: "1fr",
          gridTemplateRows: "1fr",
          position: "relative",
          backgroundColor: background ? background : "unset",
        }}
        to={`${page}`}
        ref={containerRef}
      >
        <Img
          fluid={getFluidFromAsset(image, 600)}
          sx={{ width: "100%", height: "100%", gridColumn: "1 / span 1", gridRow: "1 / span 1" }}
          imgStyle={{ objectFit: "contain" }}
        />
        <div
          className="tags-container"
          sx={{
            gridColumn: "1 / span 1",
            gridRow: "1 / span 1",
            position: "absolute",
            left: 0,
            top: ["10px", "10px", 5],
          }}
        >
          {tags.length > 0 && tags.map((tag, index) => <Tag key={index} tag={tag} />)}
        </div>
      </Touchable>
    )
  }
  return (
    <Touchable
      sx={{
        width: "100%",
        height: height,
        mt: 6,
        mb: 5,
        display: "grid",
        gridTemplateColumns: "1fr",
        gridTemplateRows: "1fr",
        position: "relative",
        backgroundColor: background ? background : "unset",
        cursor: "not-allowed",
      }}
      ref={containerRef}
    >
      <Img
        fluid={getFluidFromAsset(image, 600)}
        sx={{ width: "100%", height: "100%", gridColumn: "1 / span 1", gridRow: "1 / span 1" }}
        imgStyle={{ objectFit: "contain" }}
      />
      <div
        className="tags-container"
        sx={{
          gridColumn: "1 / span 1",
          gridRow: "1 / span 1",
          position: "absolute",
          left: 0,
          top: ["10px", "10px", 5],
        }}
      >
        {tags.length > 0 && tags.map((tag, index) => <Tag key={index} tag={tag} />)}
      </div>
    </Touchable>
  )
}

function ProductCard({ background, backProduct, color, product }) {
  const cart = useCart()
  const {
    productArray,
    productImage,
    image,
    reviewRating,
    reviewCount,
    productName,
    productDescription,
    price,
    couponName,
    productPage,
  } = product
  const variantImage = productImage ? productImage : image
  const sumOriginalPrice =
    typeof productArray === "object" && Array.isArray(productArray)
      ? productArray.reduce((acc, curr) => {
          const { singleProduct, singleVariant } = curr
          const [currentVariant] = singleProduct.variants.filter(
            item => item.ourID === singleVariant
          )
          return acc + currentVariant.price
        }, 0)
      : 0
  const variantArray =
    typeof productArray === "object" && Array.isArray(productArray)
      ? productArray.reduce((acc, curr) => {
          const { singleProduct, singleVariant } = curr
          const [currentVariant] = singleProduct.variants.filter(
            item => item.ourID === singleVariant
          )
          return [...acc, currentVariant]
        }, [])
      : []
  const allAvailable =
    typeof variantArray === "object" && Array.isArray(variantArray)
      ? variantArray.reduce((acc, curr) => (curr.available ? acc : false), true)
      : false
  const allInStock =
    typeof variantArray === "object" && Array.isArray(variantArray)
      ? variantArray.reduce((acc, curr) => (curr.inStock ? acc : false), true)
      : false
  const ATCProducts =
    typeof productArray === "object" && Array.isArray(productArray)
      ? productArray.map(currProduct => {
          const { singleProduct, singleVariant } = currProduct
          return { productID: singleProduct.ourID, variantID: singleVariant, quantity: 1 }
        })
      : []
  const numberOfReviews = reviewCount
    ? reviewCount
    : typeof productArray === "object" && Array.isArray(productArray) && productArray.length >= 1
    ? productArray[0].singleProduct.reviewCount
    : 0
  const averageRating = reviewRating
    ? reviewRating
    : typeof productArray === "object" && Array.isArray(productArray) && productArray.length >= 1
    ? productArray[0].singleProduct.reviewRating
    : 0
  const concatenatedTags =
    typeof productArray === "object" && Array.isArray(productArray)
      ? productArray.reduce((acc, curr) => {
          const { singleProduct } = curr
          const { tags } = singleProduct
          const arr = acc.concat(tags)
          return arr
        }, [])
      : []
  const set = new Set()
  concatenatedTags.forEach(item => {
    if (item) {
      set.add(item)
    }
  })
  const uniqueTags = [...set] || []
  let urlOptions, productLink
  if (productPage) {
    urlOptions =
      productPage.urlParameter && productPage.urlParameter.length > 0
        ? productPage.urlParameter.map(param => `${param.key}=${param.value}`).join("&")
        : ""
    productLink =
      urlOptions.length > 0
        ? `/${productPage.internalRef.slug.current}?${urlOptions}`
        : `/${productPage.internalRef.slug.current}`
  } else {
    productLink = null
  }

  const getDelayMessage = object => {
    if (object.delayMode === "none") return <span>Auf Lager</span>
    if (object.delayMode === "text")
      return <span sx={{ color: "accent" }}>{object.delayString}</span>
    return <span>Auf Lager</span>
  }
  return (
    <Flex.column align="center" sx={{ backgroundColor: background, pb: 5 }}>
      <ProductImage
        image={variantImage}
        tags={uniqueTags}
        background={backProduct}
        page={productLink}
      />
      <div sx={{ color: "almostBlack" }}>
        <Flex.row justify="center" sx={{ ml: 2, mr: 3, mb: 2 }}>
          {[1, 2, 3, 4, 5].map((num, i) => (
            <Icon
              key={i}
              name="Star"
              size={["14px", "14px", "16px"]}
              color={num <= averageRating ? "gold" : "#ddd"}
            />
          ))}
        </Flex.row>
        <div
          sx={{
            textAlign: "center",
            mt: 0,
            fontFamily: "body",
            fontWeight: "regular",
            fontSize: "12px",
            lineHeight: 1.0,
          }}
        >
          ({numberOfReviews || 0}{" "}
          {numberOfReviews > 1
            ? "Bewertungen"
            : numberOfReviews === 1
            ? "Bewertung"
            : "Bewertungen"}
          )
        </div>
        <h3
          sx={{
            fontFamily: "heading",
            fontSize: ["16px", "16px", "20px"],
            lineHeight: [1.0, 1.14, 1.1],
            letterSpacing: ["-1px", "-0.67px", "-0.98px"],
            fontWeight: "regular",
            textAlign: "center",
            mb: 1,
            mt: 5,
          }}
        >
          {productName}
        </h3>
        <p
          sx={{
            textAlign: "center",
            fontFamily: "body",
            fontSize: ["11px", "11px", "12px"],
            lineHeight: [1.45, 1.36, 1.33],
            fontWeight: "light",
            mb: 5,
          }}
        >
          {productDescription}
        </p>
        <p
          sx={{
            textAlign: "center",
            fontFamily: "body",
            fontSize: ["16px", "16px", "20px"],
            lineHeight: ["1.0", 1.0, 1.1075],
            fontWeight: "medium",
            mb: 1,
          }}
        >
          {price > 0
            ? displayCent(price)
            : typeof variantArray === "object" &&
              Array.isArray(variantArray) &&
              variantArray.length === 1 &&
              variantArray[0].hasOwnProperty("price")
            ? displayCent(variantArray[0].price)
            : ""}
          {variantArray.length === 1 &&
            variantArray[0].hasOwnProperty("comparePrice") &&
            variantArray[0].comparePrice && (
              <span
                sx={{
                  fontWeight: "regular",
                  textDecorationLine: "line-through",
                  textDecorationThickness: "0.1em",
                  fontSize: ["0.875em", "0.875em", "0.9em"],
                  ml: 2,
                }}
              >
                {displayCent(variantArray[0].comparePrice)}
              </span>
            )}
          {typeof productArray === "object" &&
            Array.isArray(productArray) &&
            productArray.length > 1 &&
            sumOriginalPrice > 0 &&
            sumOriginalPrice !== price && (
              <span
                sx={{
                  fontWeight: "regular",
                  textDecorationLine: "line-through",
                  textDecorationThickness: "0.1em",
                  fontSize: ["0.875em", "0.875em", "0.9em"],
                  ml: 2,
                }}
              >
                {displayCent(sumOriginalPrice)}
              </span>
            )}
        </p>
        <Flex.row justify="center" align="center">
          {allAvailable !== false && allInStock !== false ? (
            <Touchable
              role="button"
              event="AddToCart"
              onClick={() => {
                cart.addCollection(ATCProducts)
                if (couponName) {
                  cart.tryCoupon(couponName)
                }
                cart.show()
              }}
              title="Zum Warenkorb hinzufügen"
              sx={{
                fontFamily: "body",
                fontSize: ["9.54px", "12px", "15px"],
                lineHeight: [1.4, 1.288, 1.286],
                letterSpacing: ["1px", "0.67px", "0.98px"],
                textAlign: "center",
                textTransform: "uppercase",
                color: "white",
                pt: ["9.82px", "8.71px", "12.57px"],
                pb: ["7.82px", "6.71px", "10.57px"],
                px: ["10.71px", "9.21px", "13.61px"],
                bg: "almostBlack",
                mt: [5, 5, 6],
                mr: 2,
                mb: 5,
              }}
            >
              <RiIcon
                Component={FiShoppingCart}
                color="#ffffff"
                size={["9.54px", "12px", "15px"]}
              />
            </Touchable>
          ) : (
            <div
              sx={{
                height: ["30.9833px", "31.1667px", "42.7833"],
                width: "100%",
                mt: [1, 5, 8],
                mb: 5,
              }}
            />
          )}
          {productLink && (
            <Touchable
              sx={{
                fontFamily: "body",
                fontSize: ["10px", "12px", "15px"],
                lineHeight: [1.4, 1.288, 1.286],
                letterSpacing: ["1px", "0.67px", "0.98px"],
                textAlign: "center",
                textTransform: "uppercase",
                color: "white",
                py: ["9px", 3, "11.81px"],
                px: ["18px", 4, "17.72px"],
                bg: "almostBlack",
                mt: [5, 5, 6],
                mb: 5,
              }}
              to={productLink}
            >
              Mehr erfahren
            </Touchable>
          )}
        </Flex.row>
        <div
          sx={{
            fontFamily: "body",
            fontWeight: "medium",
            fontSize: ["11px", "11px", "12px"],
            lineHeight: 1.0,
            textAlign: "center",
            color: "green_600",
          }}
        >
          {allAvailable ? (
            variantArray[0].delayMode === "none" ? (
              <React.Fragment>
                <Icon
                  name="CheckmarkCircleFull"
                  size={["11px", "11px", "12px"]}
                  color="green_600"
                  sx={{ mr: 2 }}
                />
                {getDelayMessage(variantArray[0])}
              </React.Fragment>
            ) : (
              <React.Fragment>{getDelayMessage(variantArray[0])}</React.Fragment>
            )
          ) : (
            <React.Fragment>
              <span sx={{ color: "accent" }}>Ausverkauft</span>
            </React.Fragment>
          )}
        </div>
      </div>
    </Flex.column>
  )
}

function ProductGrid({
  title,
  products,
  backgroundSection,
  backgroundTile,
  backgroundProduct,
  fontColor,
}) {
  return (
    <SectionGrid sx={{ backgroundColor: backgroundSection, py: [8, 11, 13] }}>
      <SectionGrid.wide
        sx={{
          px: ["10px", "16px", "unset"],
        }}
      >
        <h2
          sx={{
            fontFamily: "heading",
            fontWeight: "regular",
            fontSize: [3, 6, 6],
            lineHeight: [1.3, 1.23, 1.0],
            letterSpacing: ["-0.5px", "unset"],
            color: "almostBlack",
            mb: 0,
          }}
        >
          {title}
        </h2>
        <Grid
          columns={["repeat(2,1fr)", "repeat(3,1fr)", "repeat(4,1fr)"]}
          sx={{ columnGap: ["8px", "24px", "30px"], rowGap: "16px" }}
        >
          {products &&
            products.length > 0 &&
            products.map((product, index) => (
              <ProductCard
                key={index}
                background={backgroundTile}
                backProduct={backgroundProduct}
                color={fontColor}
                product={product}
              />
            ))}
        </Grid>
      </SectionGrid.wide>
    </SectionGrid>
  )
}

export { ProductGrid }
